import { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Tooltip, useMap, useMapEvents } from 'react-leaflet';
import { divIcon, latLngBounds } from 'leaflet';
import type { LeafletEvent } from 'leaflet';
import { useNavigate } from 'react-router-dom';
import { Minimize2 } from 'lucide-react';
import { Employee, EmployeeCluster, TemporaryLocation, Pet } from '../types/employee';
import { clusterEmployees } from '../utils/geoUtils';
import { useEmployee } from '../contexts/EmployeeContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { reverseGeocode } from '../utils/geocode';
import ClusteredEmployeeMarker from './ClusteredEmployeeMarker';
import { petSpeciesSvgString } from '../utils/petUtils';
import { useHQ } from '../contexts/HQContext';
import { captureMutationError } from '../utils/errorReporting';
import { getBasemapUrl, getBasemapAttribution } from '../lib/basemap';

interface EmployeeMapProps {
  employees: Employee[];
}

// Pin radius is 16px. At Leaflet's max zoom (18), 1 degree latitude = 256*2^18/360 px.
// We want shells spaced 3 pin-radii apart so pins never overlap.
const PIN_RADIUS_PX = 16;
const PX_PER_DEG_LAT_Z18 = (256 * Math.pow(2, 18)) / 360;
const SHELL_STEP_DEG = (3 * PIN_RADIUS_PX) / PX_PER_DEG_LAT_Z18;

// Returns [dLat, dLng] offsets for each index in a group sharing the same coordinates.
// Index 0 → exact position. Remaining indices fill shells outward, starting at
// 12 o'clock and proceeding clockwise. Shell N holds 6*N slots at radius N*SHELL_STEP_DEG.
function buildSpiralOffsets(count: number, baseLat: number): [number, number][] {
  const offsets: [number, number][] = [[0, 0]];
  if (count <= 1) return offsets;
  // Correct longitude offsets for latitude compression
  const lngScale = Math.cos((baseLat * Math.PI) / 180) || 1;
  for (let shell = 1; offsets.length < count; shell++) {
    const slots = 6 * shell;
    const r = shell * SHELL_STEP_DEG;
    for (let i = 0; i < slots && offsets.length < count; i++) {
      const angleDeg = (i / slots) * 360; // 0 = north, clockwise
      offsets.push([
        r * Math.cos((angleDeg * Math.PI) / 180),
        (r / lngScale) * Math.sin((angleDeg * Math.PI) / 180),
      ]);
    }
  }
  return offsets;
}

interface MapPin {
  id: string;
  lat: number;
  lng: number;
}

// Maps pin id → [dLat, dLng] display offset for pins sharing exact coordinates.
function buildSpiralOffsetMap(pins: MapPin[]): Map<string, [number, number]> {
  const groups = new Map<string, MapPin[]>();
  for (const pin of pins) {
    const key = `${pin.lat},${pin.lng}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(pin);
  }
  const result = new Map<string, [number, number]>();
  for (const [, group] of groups) {
    const offsets = buildSpiralOffsets(group.length, group[0].lat);
    group.forEach((pin, i) => result.set(pin.id, offsets[i]));
  }
  return result;
}

const MAX_CLUSTER_ZOOM = 12;
const MIN_DRAG_ZOOM = 14;

const EmployeeMap = ({ employees }: EmployeeMapProps) => {
  const navigate = useNavigate();
  const { centerCoordinates, reloadEmployees } = useEmployee();
  const { theme } = useTheme();
  const { employeeProfile } = useAuth();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [clusters, setClusters] = useState<EmployeeCluster[]>([]);
  const [clusteredEmpIds, setClusteredEmpIds] = useState<Set<string>>(new Set());
  const [currentZoom, setCurrentZoom] = useState(1);
  const [dragSaving, setDragSaving] = useState(false);
  const [dragError, setDragError] = useState<string | null>(null);
  const mapRef = useRef<L.Map | null>(null);

  // Keyed by employee slug — tracks optimistic position during/after a drag
  const [dragPositions, setDragPositions] = useState<Map<string, [number, number]>>(new Map());
  const { hq } = useHQ();

  const createClusters = () => {
    if (!mapRef.current) {
      return;
    }

    const clusteredIds = new Set([]);

    if (currentZoom > MAX_CLUSTER_ZOOM) {
      setClusters([]);
      setClusteredEmpIds(clusteredIds);
      return;
    }

    const result = clusterEmployees(mapRef.current, employees, currentZoom);

    setClusters(result.clusters);
    setClusteredEmpIds(new Set(result.clusterByEmployeeId.keys()));
  };

  const handleClusterClick = (cluster: EmployeeCluster) => {
    if (!mapRef.current) return;

    const map = mapRef.current;
    const memberBounds = latLngBounds(cluster.memberBounds() as [[number, number], [number, number]]);
    const targetZoom = Math.max(map.getBoundsZoom(memberBounds, false, [80, 80]), MAX_CLUSTER_ZOOM + 1);
    map.flyToBounds(memberBounds, { padding: [80, 80], maxZoom: targetZoom });
  };

  useEffect(() => {
    createClusters();
  }, [currentZoom, employees]);

  // Create custom marker icons for employees
  const createEmployeeIcon = (employee: Employee, isDraggable: boolean) => {
    const hovered = selectedId === employee.id;
    const isEvilMartian = employee.employer === 'EvilMartians';
    const isSaving = isDraggable && dragSaving;

    return divIcon({
      className: 'leaflet-div-icon',
      html: `
        <div class="map-marker" style="${isDraggable ? 'cursor: grab;' : ''}">
          <img
            src="${hovered ? employee.avatarHover : employee.avatar}"
            alt="${employee.name}"
            class="map-marker-avatar"
            style="${hovered ? 'transform: scale(1.2); border-color: #D8315B;' : ''}${isDraggable ? ' box-shadow: 0 0 0 2.5px #3b82f6, 0 2px 8px rgba(59,130,246,0.4);' : ''}"
          />
          ${isEvilMartian ? '<div class="evil-martians-badge"></div>' : ''}
          ${isSaving ? '<div style="position:absolute;inset:0;border-radius:50%;background:rgba(255,255,255,0.55);display:flex;align-items:center;justify-content:center;"></div>' : ''}
        </div>
      `,
      iconSize: [32, 32],
      iconAnchor: [16, 16],
    });
  };

  const createPetIcon = (pet: Pet, ownerName: string, petIndex: number, petCount: number) => {
    const hovered = selectedId === `pet-${pet.id}`;
    const svgIcon = petSpeciesSvgString(pet.species, 10, 'white');
    const size = 24;
    const anchor = size / 2;

    // Fan pets in a semicircle below the owner
    const fanRadius = 18;
    let offsetX = 0;
    let offsetY = -fanRadius;
    if (petCount > 1) {
      const startAngle = Math.PI * 0.25;
      const endAngle = Math.PI * 0.75;
      const angle = petCount === 1 ? Math.PI / 2 : startAngle + (endAngle - startAngle) * (petIndex / (petCount - 1));
      offsetX = -Math.cos(angle) * fanRadius;
      offsetY = -Math.sin(angle) * fanRadius;
    }

    return divIcon({
      className: 'leaflet-div-icon',
      html: pet.photoUrl
        ? `
          <div style="width:${size}px;height:${size}px;position:relative;opacity:0.92;">
            <img
              src="${pet.photoUrl}"
              alt="${pet.name}"
              style="width:${size}px;height:${size}px;border-radius:50%;object-fit:cover;border:2px solid ${hovered ? '#10b981' : 'rgba(16,185,129,0.7)'};${hovered ? 'transform:scale(1.2);' : ''}"
            />
            <div style="
              position:absolute;
              bottom:-2px;
              right:-2px;
              width:12px;
              height:12px;
              background:#10b981;
              border:1.5px solid white;
              border-radius:50%;
              display:flex;
              align-items:center;
              justify-content:center;
              z-index:10;
            ">${svgIcon}</div>
          </div>
        `
        : `
          <div style="
            width:${size}px;
            height:${size}px;
            border-radius:50%;
            background:${hovered ? '#059669' : '#10b981'};
            border:2px solid ${hovered ? '#34d399' : 'white'};
            box-shadow:0 2px 6px rgba(0,0,0,0.25);
            display:flex;
            align-items:center;
            justify-content:center;
            transform:${hovered ? 'scale(1.2)' : 'scale(1)'};
            transition:transform 0.15s;
            cursor:pointer;
          ">${svgIcon}</div>
        `,
      iconSize: [size, size],
      iconAnchor: [anchor - offsetX, anchor - offsetY],
    });
  };

  // Create temporary location marker icon
  const createTempLocIcon = (employee: Employee, loc: TemporaryLocation) => {
    const hovered = selectedId === `tmp-${loc.id}`;
    return divIcon({
      className: 'leaflet-div-icon',
      html: `
        <div class="map-marker" style="opacity: 0.92;">
          <img
            src="${employee.avatar}"
            alt="${employee.name}"
            class="map-marker-avatar"
            style="border-color: ${hovered ? '#f59e0b' : 'rgba(245,158,11,0.85)'}; ${hovered ? 'transform: scale(1.2);' : ''}"
          />
          <div style="
            position: absolute;
            bottom: -3px;
            right: -3px;
            width: 14px;
            height: 14px;
            background: #f59e0b;
            border: 2px solid white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 8px;
            z-index: 10;
          ">✈</div>
        </div>
      `,
      iconSize: [32, 32],
      iconAnchor: [16, 16],
    });
  };

  // Create center marker icon
  const centerIcon = divIcon({
    className: 'leaflet-div-icon',
    html: `
      <div class="map-center-marker" style="width: 20px; height: 20px;"></div>
    `,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });

  const handleMarkerClick = (id: string) => {
    navigate(`/employee/${id}`);
  };

  const handleMarkerHover = (id: string) => {
    setSelectedId(id);
  };

  const handleMarkerLeave = () => {
    setSelectedId(null);
  };

  const handleTeamCenterClick = () => {
    if (!mapRef.current) return;

    const map = mapRef.current;

    const lats = employees.map((e: Employee) => e.location.coordinates.latitude);
    const lons = employees.map((e: Employee) => e.location.coordinates.longitude);

    const bounds = latLngBounds(
      [Math.min(...lats), Math.min(...lons)],
      [Math.max(...lats), Math.max(...lons)],
    );

    map.flyToBounds(bounds, { padding: [40, 40], maxZoom: 8, duration: 1.2 });
  };

  const handleDragEnd = async (employee: Employee, event: LeafletEvent) => {
    const marker = event.target;
    const { lat, lng } = marker.getLatLng();
    const prevPos: [number, number] = [
      employee.location.coordinates.latitude,
      employee.location.coordinates.longitude,
    ];

    setDragError(null);
    setDragSaving(true);

    // Validate: must land within a country
    const geo = await reverseGeocode(lat, lng);
    if (!geo) {
      // Snap back
      marker.setLatLng(prevPos);
      setDragPositions(m => { const n = new Map(m); n.delete(employee.id); return n; });
      setDragError('That location is not within any country — please drop your pin on land.');
      setDragSaving(false);
      return;
    }

    // Optimistically update displayed position
    setDragPositions(m => new Map(m).set(employee.id, [lat, lng]));

    const { error } = await supabase
      .from('employees')
      .update({ latitude: lat, longitude: lng, coordinates_manual: true })
      .eq('id', employeeProfile!.id);

    if (error) {
      captureMutationError(error, { operation: 'dragUpdateCoordinates', table: 'employees', employeeId: employeeProfile!.id });
      marker.setLatLng(prevPos);
      setDragPositions(m => { const n = new Map(m); n.delete(employee.id); return n; });
      setDragError('Failed to save location. Please try again.');
    } else {
      await reloadEmployees();
    }

    setDragSaving(false);
  };

  const EventHandler = () => {
    useMapEvents({
      zoomend: (e) => {
        console.log(`Current zoom: ${e.target.getZoom()}`);
        setCurrentZoom(e.target.getZoom())
      },
    });

    return null;
  };

  const today = new Date().toISOString().slice(0, 10);
  const activeTempLocs: { employee: Employee; loc: TemporaryLocation }[] = employees.flatMap(emp =>
    (emp.temporaryLocations ?? [])
      .filter(loc =>
        loc.startDate <= today && loc.endDate >= today &&
        (loc.coordinates.latitude !== 0 || loc.coordinates.longitude !== 0)
      )
      .map(loc => ({ employee: emp, loc }))
  );

  const markedEmployees = employees.filter(emp => !clusteredEmpIds.has(emp.id));

  // Build all pins (employees only) for unified spiral layout at each coord
  const allPins: MapPin[] = markedEmployees.map(emp => ({
    id: emp.id,
    lat: emp.location.coordinates.latitude,
    lng: emp.location.coordinates.longitude,
  }));
  const spiralOffsetMap = buildSpiralOffsetMap(allPins);

  const initialBounds = employees.length > 0
    ? latLngBounds(
        employees.map(e => [e.location.coordinates.latitude, e.location.coordinates.longitude] as [number, number])
      ).pad(0.15)
    : latLngBounds([[-60, -160], [70, 160]]);

  return (
    <div className="relative rounded-lg overflow-hidden shadow-lg border border-gray-200 dark:border-gray-700">
      <button
        onClick={handleTeamCenterClick}
        title="Zoom to fit all team members"
        className="absolute top-2 right-2 z-[1000] flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium
          bg-bolt-surface/90 backdrop-blur-sm border border-bolt-border text-bolt-textMuted
          hover:text-bolt-text hover:bg-bolt-surface transition-colors shadow-sm"
      >
        <Minimize2 className="w-3.5 h-3.5" />
        Fit team
      </button>

      {dragError && (
        <div
          className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[1000] px-3 py-2 rounded-lg text-xs font-medium
            bg-error-500/90 text-white shadow-lg backdrop-blur-sm border border-error-400/30 max-w-xs text-center"
          onClick={() => setDragError(null)}
          role="alert"
          style={{ cursor: 'pointer' }}
        >
          {dragError}
        </div>
      )}

      {dragSaving && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[1000] px-3 py-2 rounded-lg text-xs font-medium
          bg-bolt-surface/90 text-bolt-textMuted shadow-sm backdrop-blur-sm border border-bolt-border">
          Saving location…
        </div>
      )}

      <MapContainer
        ref={mapRef}
        bounds={initialBounds}
        minZoom={2}
        maxBounds={[[-60, -180], [70, 180]]}
        scrollWheelZoom={true}
        whenReady={(e) => setCurrentZoom(e.target.getZoom())}
        className="z-10"
      >
        <EventHandler/>

        <TileLayer
          attribution={getBasemapAttribution()}
          url={getBasemapUrl(theme)}
        />

        {/* Center Marker */}
        <Marker
          position={centerCoordinates}
          icon={centerIcon}
          zIndexOffset={1000}
          eventHandlers={{
            click: handleTeamCenterClick
          }}
        >
          <Tooltip direction="top" offset={[0, -10]}>
            <div className="text-xs font-medium">Team Center</div>
          </Tooltip>
        </Marker>

        {/* Cluster Markers */}
        {clusters.map((cluster, index) => (
          //<>
            <ClusteredEmployeeMarker
              key={`cluster-${index}`}
              cluster={cluster}
              onClusterClick={handleClusterClick}
              hq={hq}
            />
          //<Rectangle
          //  bounds={cluster.bounds()}
          //  pathOptions={{color: '#ff7800', weight: 1}}
          //  />
          //</>
        ))}

        {/* Employee Markers */}
        {markedEmployees.map(employee => {
          const isOwnPin = employeeProfile?.slug === employee.id;
          const canDrag = isOwnPin && currentZoom >= MIN_DRAG_ZOOM;
          const pos = dragPositions.get(employee.id);
          const [dLat, dLng] = spiralOffsetMap.get(employee.id) ?? [0, 0];
          const position: [number, number] = pos ?? [
            employee.location.coordinates.latitude + dLat,
            employee.location.coordinates.longitude + dLng,
          ];
          return (
            <Marker
              key={employee.id}
              position={position}
              icon={createEmployeeIcon(employee, canDrag)}
              draggable={canDrag}
              zIndexOffset={isOwnPin ? 200 : 0}
              eventHandlers={{
                click: () => { if (!isOwnPin) handleMarkerClick(employee.id); },
                mouseover: () => handleMarkerHover(employee.id),
                mouseout: () => handleMarkerLeave(),
                ...(canDrag ? { dragend: (e) => handleDragEnd(employee, e) } : {}),
              }}
            >
              <Tooltip direction="top" offset={[0, -5]}>
                <div>
                  <div className="font-medium">{employee.name}</div>
                  <div className="text-xs text-gray-600">{employee.role}</div>
                  <div className="text-xs text-gray-600">{[employee.location.city, employee.location.country].filter(Boolean).join(', ')}</div>
                  {isOwnPin && (
                    <div className="text-xs text-blue-500 mt-0.5">
                      {canDrag ? 'Drag to update your location' : 'Zoom in to drag your pin to an exact location'}
                    </div>
                  )}
                </div>
              </Tooltip>
            </Marker>
          );
        })}

        {/* Pet Markers — positioned at owner's location (offset applied via icon anchor) */}
        {markedEmployees.flatMap(employee => {
          const pets = employee.pets ?? [];
          return pets.map((pet, idx) => {
            const [dLat, dLng] = spiralOffsetMap.get(employee.id) ?? [0, 0];
            const position: [number, number] = [
              employee.location.coordinates.latitude + dLat,
              employee.location.coordinates.longitude + dLng,
            ];
            return (
              <Marker
                key={`pet-${pet.id}`}
                position={position}
                icon={createPetIcon(pet, employee.name, idx, pets.length)}
                zIndexOffset={-100}
                eventHandlers={{
                  click: () => handleMarkerClick(employee.id),
                  mouseover: () => handleMarkerHover(`pet-${pet.id}`),
                  mouseout: () => handleMarkerLeave(),
                }}
              >
                <Tooltip direction="top" offset={[0, -5]}>
                  <div>
                    <div className="font-medium">{pet.name}</div>
                    <div className="text-xs text-gray-600">{pet.role ?? pet.species}</div>
                    <div className="text-xs text-gray-500">Pet of {employee.name}</div>
                  </div>
                </Tooltip>
              </Marker>
            );
          });
        })}

        {/* HQ Marker — hidden when its coordinates fall inside a cluster bbox */}
        {hq && !clusters.some(c =>
          hq.latitude  >= c.bbox.minLat && hq.latitude  <= c.bbox.maxLat &&
          hq.longitude >= c.bbox.minLng && hq.longitude <= c.bbox.maxLng
        ) && (
          <Marker
            key="hq-pin"
            position={[hq.latitude, hq.longitude]}
            zIndexOffset={2000}
            icon={divIcon({
              html: `<div style="width:40px;height:40px;border-radius:50%;background:#0f172a;border:2.5px solid #38bdf8;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 12px rgba(0,0,0,0.5);">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#38bdf8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/>
                  <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/>
                  <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/>
                  <path d="M10 6h4"/><path d="M10 10h4"/><path d="M10 14h4"/><path d="M10 18h4"/>
                </svg>
              </div>`,
              className: '',
              iconSize: [40, 40],
              iconAnchor: [20, 20],
            })}
          >
            <Tooltip direction="top" offset={[0, -5]}>
              <div>
                <div className="font-semibold">{hq.name}</div>
                <div className="text-xs text-gray-500">{hq.address.split(',').slice(0, 2).join(',')}</div>
              </div>
            </Tooltip>
          </Marker>
        )}

        {/* Temporary Location Markers */}
        {activeTempLocs.map(({ employee, loc }) => (
          <Marker
            key={`tmp-${loc.id}`}
            position={[loc.coordinates.latitude, loc.coordinates.longitude]}
            icon={createTempLocIcon(employee, loc)}
            zIndexOffset={500}
            eventHandlers={{
              click: () => handleMarkerClick(employee.id),
              mouseover: () => handleMarkerHover(`tmp-${loc.id}`),
              mouseout: () => handleMarkerLeave(),
            }}
          >
            <Tooltip direction="top" offset={[0, -5]}>
              <div>
                <div className="font-medium">{employee.name}</div>
                <div className="text-xs text-gray-600" style={{ color: '#f59e0b' }}>
                  ✈ Traveling · {loc.city}, {loc.country}
                </div>
                {loc.label && <div className="text-xs text-gray-500">{loc.label}</div>}
                <div className="text-xs text-gray-500">{loc.startDate} → {loc.endDate}</div>
              </div>
            </Tooltip>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};

export default EmployeeMap;
